type Direction = 'prev' | 'next' | 'prevPage' | 'nextPage'
type FocusPosition = 'first' | 'last' | 'none'
// eslint-disable-next-line no-unused-vars,no-undef
export type OnMonthChange = (direction: Direction) => void

export class RovingTabindexGrid {
  groupNode: Element
  gridCells: HTMLElement[]
  firstCell: HTMLElement | null
  lastCell: HTMLElement | null
  columnsPerRow: number
  onMonthChange?: OnMonthChange

  constructor(groupNode: Element, columnsPerRow: number = 7, onMonthChange?: OnMonthChange) {
    this.groupNode = groupNode
    this.columnsPerRow = columnsPerRow
    this.onMonthChange = onMonthChange
    this.gridCells = []
    this.firstCell = null
    this.lastCell = null

    this.initialize()
  }

  initialize(focusPosition: FocusPosition = 'none') {
    this.gridCells.forEach(cell => {
      cell.removeEventListener('keydown', this.handleKeydown)
      cell.removeEventListener('focus', this.handleFocus)
      cell.removeEventListener('blur', this.handleBlur)
    })

    const cells = this.groupNode.querySelectorAll<HTMLElement>('[role=gridcell]:not([disabled])')

    this.gridCells = []
    this.firstCell = null
    this.lastCell = null

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i]

      cell.tabIndex = -1

      cell.addEventListener('keydown', this.handleKeydown)
      cell.addEventListener('focus', this.handleFocus)
      cell.addEventListener('blur', this.handleBlur)

      this.gridCells.push(cell)

      if (!this.firstCell) {
        this.firstCell = cell
      }
      this.lastCell = cell
    }

    if (focusPosition === 'first' && this.firstCell) {
      this.firstCell.tabIndex = 0
      this.firstCell.focus()
    } else if (focusPosition === 'last' && this.lastCell) {
      this.lastCell.tabIndex = 0
      this.lastCell.focus()
    } else if (this.firstCell) {
      this.firstCell.tabIndex = 0
    }
  }

  destroy() {
    this.gridCells.forEach(cell => {
      cell.removeEventListener('keydown', this.handleKeydown)
      cell.removeEventListener('focus', this.handleFocus)
      cell.removeEventListener('blur', this.handleBlur)
      cell.tabIndex = -1
    })

    this.gridCells = []
    this.firstCell = null
    this.lastCell = null
  }

  setFocused(currentItem: HTMLElement) {
    for (let i = 0; i < this.gridCells.length; i++) {
      this.gridCells[i].tabIndex = -1
    }
    currentItem.tabIndex = 0
    currentItem.focus()
  }

  moveToPreviousCell(currentItem: HTMLElement) {
    const index = this.gridCells.indexOf(currentItem)
    if (index > 0) {
      this.setFocused(this.gridCells[index - 1])
    } else if (this.onMonthChange) {
      this.onMonthChange('prev')
    }
  }

  moveToNextCell(currentItem: HTMLElement) {
    const index = this.gridCells.indexOf(currentItem)
    if (index < this.gridCells.length - 1) {
      this.setFocused(this.gridCells[index + 1])
    } else if (this.onMonthChange) {
      this.onMonthChange('next')
    }
  }

  moveUp(currentItem: HTMLElement) {
    const index = this.gridCells.indexOf(currentItem)
    const targetIndex = index - this.columnsPerRow
    if (targetIndex >= 0) {
      this.setFocused(this.gridCells[targetIndex])
    } else if (this.onMonthChange) {
      this.onMonthChange('prev')
    }
  }

  moveDown(currentItem: HTMLElement) {
    const index = this.gridCells.indexOf(currentItem)
    const targetIndex = index + this.columnsPerRow
    if (targetIndex < this.gridCells.length) {
      this.setFocused(this.gridCells[targetIndex])
    } else if (this.onMonthChange) {
      this.onMonthChange('next')
    }
  }

  moveToStartOfRow(currentItem: HTMLElement) {
    const index = this.gridCells.indexOf(currentItem)
    const rowStart = Math.floor(index / this.columnsPerRow) * this.columnsPerRow
    this.setFocused(this.gridCells[rowStart])
  }

  moveToEndOfRow(currentItem: HTMLElement) {
    const index = this.gridCells.indexOf(currentItem)
    const rowEnd =
      Math.floor(index / this.columnsPerRow) * this.columnsPerRow + this.columnsPerRow - 1
    const targetIndex = Math.min(rowEnd, this.gridCells.length - 1)
    this.setFocused(this.gridCells[targetIndex])
  }

  private handleKeydown = (event: KeyboardEvent) => {
    const tgt = event.currentTarget as HTMLElement
    let flag = false

    switch (event.key) {
      case 'ArrowRight':
        this.moveToNextCell(tgt)
        flag = true
        break
      case 'ArrowLeft':
        this.moveToPreviousCell(tgt)
        flag = true
        break
      case 'ArrowDown':
        this.moveDown(tgt)
        flag = true
        break
      case 'ArrowUp':
        this.moveUp(tgt)
        flag = true
        break
      case 'Home':
        this.moveToStartOfRow(tgt)
        flag = true
        break
      case 'End':
        this.moveToEndOfRow(tgt)
        flag = true
        break
      case 'PageUp':
        if (this.onMonthChange) {
          this.onMonthChange('prevPage')
          flag = true
        }
        break
      case 'PageDown':
        if (this.onMonthChange) {
          this.onMonthChange('nextPage')
          flag = true
        }
        break
      case 'Enter':
      case ' ':
        tgt.dispatchEvent(new MouseEvent('click', { bubbles: true }))
        flag = true
        break
    }

    if (flag) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  private handleFocus = (event: FocusEvent) => {
    const tgt = event.currentTarget as HTMLElement
    tgt.classList.add('focus')
  }

  private handleBlur = (event: FocusEvent) => {
    const tgt = event.currentTarget as HTMLElement
    tgt.classList.remove('focus')
  }
}
